import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Car, ArrowLeft, Ban, ShieldCheck, Navigation2, Building2, HelpCircle } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SeoFooter } from "@/components/SeoFooter";
import { useSeo, useJsonLd, ogImageUrl } from "@/lib/seo";
import { findThematic, THEMATIC_PAGES } from "@/lib/thematic";
import { CITIES, CITY_PATH } from "@/lib/cities";

// Static derived list (same for every render) — computed once at module load.
const TOP_CITIES = CITIES.slice(0, 12);

export default function ThematicPage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const page = findThematic(slug);

  useSeo(
    page ? page.metaTitle : "vspo",
    page ? page.metaDescription : "",
    page ? page.path : undefined,
    page ? ogImageUrl({ title: page.h1 }) : undefined
  );

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  useJsonLd(
    "thematic-jsonld",
    page
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Accueil", item: `${origin}/` },
              { "@type": "ListItem", position: 2, name: page.kicker, item: `${origin}${page.path}` },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: (page.faq || []).map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]
      : null
  );

  if (!page) return <Navigate to="/" replace />;

  const Icon = page.slug.startsWith("eviter") ? Ban : ShieldCheck;

  return (
    <div className="min-h-screen w-full overflow-y-auto bg-background">
      <header className="sticky top-0 z-10 border-b border-border bg-card/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Car className="h-5 w-5" />
            </span>
            <div className="leading-tight">
              <div className="font-display text-lg font-bold text-foreground">vspo</div>
              <div className="text-[11px] text-muted-foreground">GPS voiture sans permis · 45 km/h</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground"
              data-testid="thematic-open-app"
            >
              <ArrowLeft className="h-4 w-4" /> Ouvrir le GPS
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 pb-20 pt-6">
        <nav className="mb-3 text-xs text-muted-foreground" aria-label="Fil d'ariane">
          <Link to="/" className="hover:text-primary">Accueil</Link> ·{" "}
          <span className="text-foreground">{page.kicker}</span>
        </nav>

        <div className="mb-2 flex items-center gap-2">
          <Icon className="h-5 w-5 text-primary" />
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {page.kicker}
          </span>
        </div>
        <h1 className="font-display text-3xl font-bold text-foreground" data-testid="thematic-title">
          {page.h1}
        </h1>
        <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">{page.intro}</p>

        {page.body.map((sec) => (
          <section className="mt-8" key={sec.h2}>
            <h2 className="font-display text-xl font-bold text-foreground">{sec.h2}</h2>
            <div className="mt-2 space-y-3 text-[15px] leading-relaxed text-muted-foreground">
              {sec.paragraphs.map((p) => (
                <p key={p.slice(0, 40)}>{p}</p>
              ))}
            </div>
          </section>
        ))}

        <div className="mt-10 rounded-2xl border border-border bg-card p-5">
          <h2 className="font-display text-lg font-bold text-foreground">Essayez vspo gratuitement</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Ouvrez le GPS, entrez votre départ et votre arrivée : l'itinéraire s'adapte à votre voiture sans permis.
          </p>
          <Link
            to="/"
            className="mt-3 inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground"
            data-testid="thematic-cta"
          >
            <Navigation2 className="h-4 w-4" /> Ouvrir le GPS
          </Link>
        </div>

        {page.faq && page.faq.length > 0 && (
          <section className="mt-10">
            <div className="mb-3 flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-primary" />
              <h2 className="font-display text-base font-bold text-foreground">Questions fréquentes</h2>
            </div>
            <div className="space-y-3">
              {page.faq.map((f) => (
                <div key={f.q} className="rounded-xl border border-border bg-card p-4">
                  <h3 className="text-[15px] font-semibold text-foreground">{f.q}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-10">
          <div className="mb-3 flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary" />
            <h2 className="font-display text-base font-bold text-foreground">GPS VSP par ville</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {TOP_CITIES.map((c) => (
              <Link
                key={c.slug}
                to={`${CITY_PATH}/${c.slug}`}
                className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-primary"
                data-testid={`thematic-city-${c.slug}`}
              >
                {c.name}
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-8">
          <h2 className="sr-only">Autres guides</h2>
          <div className="flex flex-wrap gap-2">
            {THEMATIC_PAGES.filter((t) => t.slug !== page.slug).map((t) => (
              <Link
                key={t.slug}
                to={t.path}
                className="text-sm text-primary hover:underline"
                data-testid={`thematic-link-${t.slug}`}
              >
                {t.kicker}
              </Link>
            ))}
          </div>
        </section>

        <p className="mt-8 text-xs text-muted-foreground">
          Informations fournies à titre indicatif. Vous restez responsable du respect du code de la
          route et de la signalisation.
        </p>
      </main>
      <SeoFooter />
    </div>
  );
}
